import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";

import ModelHeader from "../../component/models/ModelHeader";
import ModelMainChart from "../../component/models/ModelMainChart";
import ModelBreakdownTable from "../../component/models/ModelBreakdownTable";
import TableAndAverage from "../../component/charts/TableAndAverage";
import ClassificationPieChart from "../../component/models/ClassificationPieChart";
import PriceBreakdownPanel from "../../component/models/PriceBreakdownPanel";
import RiskAssessmentChart from "../../component/charts/RiskAssementChart";
import RegulatoryInsightCard from "../../component/models/RegulatoryInsightCard";
import ModelBuilderOverlay from "../../component/models/builder/ModelBuilderOverlay";
import ProductComparisonHeader from "../../component/models/ProductComparisonHeader";
import { ToastContainer, useToast } from "../../component/Toast";

import {
  useProductDetail,
  useProductGeneral,
  useProductRisk,
  useProductRegulatory,
  useProductInsights,
  useProductClassification,
  useProductSources,
  useAllMaterials,
} from "../../hooks";
import useComparisons from "../../hooks/useComparisons";
import useSubProductDetails from "../../hooks/useSubProductDetails";
import useMaterialStability from "../../hooks/useMaterialStability";

import { productService, interactionService } from "../../services";
import {
  transformModelDataToApiFormat,
  hasNestedSubProducts,
  getSubProductDetail,
  mapMarketStabilityToIndicator,
} from "../../utils/modelTransforms";
import {
  MATERIAL_COLORS,
  IMPACT_SORT_ORDER,
  IMPACT_BADGE_COLOR,
  MAX_COMPARISONS,
} from "../../constants/models";

import "../../css/models-common.css";
import "../../css/model.css";
import "../../css/chart-main.css";

// ---------------------------------------------------------------------------
// Regulatory / Insights data transforms
// ---------------------------------------------------------------------------

const transformRegulatory = (regulatoryData) =>
  (regulatoryData ?? [])
    .slice()
    .sort((a, b) => (IMPACT_SORT_ORDER[a.impact] ?? 99) - (IMPACT_SORT_ORDER[b.impact] ?? 99))
    .map(entry => ({
      id: entry.id,
      title: entry.title,
      content: entry.text,
      badge: entry.impact,
      badgeColor: IMPACT_BADGE_COLOR[entry.impact] ?? 'yellow',
      sourceLabel: entry.source_label,
      sourceUrl: entry.source_url,
    }));

const transformInsights = (insightsData) =>
  (insightsData ?? [])
    .slice()
    .sort((a, b) => {
      const ta = a.published_at ? new Date(a.published_at).getTime() : 0;
      const tb = b.published_at ? new Date(b.published_at).getTime() : 0;
      return tb - ta;
    })
    .map(entry => ({
      id: entry.id,
      title: entry.title,
      content: entry.text,
      sourceLabel: entry.source_label,
      sourceUrl: entry.source_url,
      publishedAt: entry.published_at,
      badge: null,
      badgeColor: null,
    }));

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ModelPage = () => {
  const { modelId } = useParams();

  // Core product data
  const { data: product, loading, error, refetch: refetchProduct } = useProductDetail(modelId);
  const { data: generalData, refetch: refetchGeneral } = useProductGeneral(modelId);
  const { data: allMaterials } = useAllMaterials();

  // Secondary product data (all fetched in parallel)
  const { data: riskData, loading: riskLoading, error: riskError } = useProductRisk(modelId);
  const { data: regulatoryData, loading: regulatoryLoading } = useProductRegulatory(modelId);
  const { data: insightsData, loading: insightsLoading } = useProductInsights(modelId);
  const { data: classificationData, loading: classificationLoading } = useProductClassification(modelId);
  const { data: sourcesData, loading: sourcesLoading } = useProductSources(modelId);

  // Materials lookup map
  const materialsById = useMemo(() => {
    if (!allMaterials) return {};
    return Object.fromEntries(allMaterials.map(m => [m.id, m]));
  }, [allMaterials]);

  // Sub-product and stability data via extracted hooks
  const subProductDetails = useSubProductDetails(product?.components);
  const materialStabilityByCode = useMaterialStability(product?.components, subProductDetails, materialsById);

  // Comparison state
  const { comparisons, addComparison, removeComparison, toggleComparison, maxReached } = useComparisons(MAX_COMPARISONS);

  // Model builder overlay
  const [showModelBuilder, setShowModelBuilder] = useState(false);

  // Drag-and-drop ordering for the two central panels
  const [draggableComponents, setDraggableComponents] = useState([
    { id: "model-breakdown", component: "ModelBreakdown" },
    { id: "table-average", component: "TableAndAverage" },
  ]);
  const [draggedItem, setDraggedItem] = useState(null);

  // Toast notifications
  const { toasts, addToast, removeToast } = useToast();

  // Side effects
  useEffect(() => {
    document.title = `Model: ${product?.name || 'Model'} - Dataflock`;
  }, [product]);

  useEffect(() => {
    if (modelId) interactionService.trackProductView(modelId);
  }, [modelId]);

  // ---------------------------------------------------------------------------
  // Derived data
  // ---------------------------------------------------------------------------

  const transformedRegulatory = useMemo(() => transformRegulatory(regulatoryData), [regulatoryData]);
  const transformedInsights = useMemo(() => transformInsights(insightsData), [insightsData]);

  // Breakdown sections: one section per sub-product, plus one section for standalone materials
  const breakdownSections = useMemo(() => {
    if (!product?.components) return null;

    const enrichMaterial = (comp) => {
      const material = materialsById[comp.material_id];
      const materialCode = material?.code ?? null;
      return {
        ...comp,
        code: materialCode || comp.source_country || '—',
        materialCode,
        price: material?.last_value ?? 0,
        marketPrice: material?.last_value ?? 0,
        priceCurrency: material?.currency
          ? `${material.currency}/${material.value_unit || 'mt'}`
          : comp.currency,
        chartData: material?.recent_chart_data?.map(d => ({ value: d.value })) ?? [],
        stability: mapMarketStabilityToIndicator(
          materialCode ? materialStabilityByCode[materialCode] : null,
          comp.source_country
        ),
        source: comp.source_country || '—',
        amount: comp.quantity ?? 0,
      };
    };

    const productComps = product.components.filter(c => c.component_type === 'product');
    const materialComps = product.components.filter(c => c.component_type === 'material');
    const sections = [];

    productComps.forEach(pc => {
      const subDetail = getSubProductDetail(pc, subProductDetails);
      const subMaterials = subDetail?.components?.filter(c => c.component_type === 'material') ?? [];
      sections.push({
        title: pc.name,
        currency: pc.currency,
        quantity: pc.quantity,
        unit: pc.unit,
        price: pc.product_ref_id ? (subDetail?.last_price_value ?? null) : null,
        productRefId: pc.product_ref_id ?? null,
        components: subMaterials.map(enrichMaterial),
      });
    });

    if (materialComps.length > 0) {
      sections.push({ components: materialComps.map(enrichMaterial) });
    }

    return sections;
  }, [product?.components, materialsById, materialStabilityByCode, subProductDetails]);

  // Price breakdown: merge duplicate materials and calculate each component's share
  const priceBreakdownData = useMemo(() => {
    if (!product?.components) return null;

    const modelCurrency = product.default_currency || product.last_price_currency || 'EUR';
    const qty = product.base_quantity || 1;
    const materialMap = new Map();

    const addMaterial = (comp, overrideQuantity) => {
      const material = materialsById[comp.material_id];
      const unitPrice = material?.last_value || comp.price || 0;
      const amount = overrideQuantity ?? comp.quantity ?? 1;
      const cost = unitPrice * amount;
      const key = comp.material_id || comp.name;

      if (materialMap.has(key)) {
        materialMap.get(key).totalCost += cost;
      } else {
        materialMap.set(key, { name: comp.name, totalCost: cost });
      }
    };

    product.components
      .filter(c => c.component_type === 'material')
      .forEach(c => addMaterial(c));

    product.components
      .filter(c => c.component_type === 'product')
      .forEach(pc => {
        const subDetail = getSubProductDetail(pc, subProductDetails);
        const subMaterials = subDetail?.components?.filter(c => c.component_type === 'material') ?? [];
        const subQty = pc.quantity || 1;
        subMaterials.forEach(c => addMaterial(c, (c.quantity || 1) * subQty));
      });

    const entries = Array.from(materialMap.values()).sort((a, b) => b.totalCost - a.totalCost);
    const subtotal = entries.reduce((sum, e) => sum + e.totalCost, 0);

    return {
      components: entries.map((entry, idx) => ({
        id: idx + 1,
        name: entry.name,
        percentage: subtotal > 0 ? Math.round((entry.totalCost / subtotal) * 100) : 0,
        price: entry.totalCost * qty,
        currency: modelCurrency,
      })),
      total: { price: subtotal * qty, currency: modelCurrency },
    };
  }, [product, materialsById, subProductDetails]);

  // Initial data for the model builder (reverse-maps API format back to builder format)
  const builderInitialData = useMemo(() => {
    if (!product) return null;

    const materialComps = (product.components || []).filter(c => c.component_type === 'material');
    const productComps = (product.components || []).filter(c => c.component_type === 'product');

    const builderComponents = materialComps.map((comp, idx) => {
      const material = materialsById[comp.material_id];
      return {
        id: comp.material_id || comp.id || Date.now() + idx,
        name: comp.name,
        code: material?.code || '',
        price: material?.last_value || 0,
        color: MATERIAL_COLORS[idx % MATERIAL_COLORS.length],
        amount: comp.quantity || 1,
        currency: comp.currency || product.default_currency || 'USD',
        unit: comp.unit || 'kg',
        source: comp.source_country || '',
        destination: comp.destination_country || '',
        selected: false,
      };
    });

    const builderSubProducts = productComps.map((pc) => {
      const isRef = !!pc.product_ref_id;
      const subDetail = getSubProductDetail(pc, subProductDetails);
      const hasDetail = subDetail?.components != null;

      if (isRef && !hasDetail) {
        return {
          id: pc.id || pc.product_ref_id,
          productRefId: pc.product_ref_id || null,
          name: pc.name,
          type: 'product',
          locked: true,
          components: [],
          subtotal: 0,
          currency: pc.currency || product.default_currency || 'USD',
          unit: pc.unit || 'unit',
          amount: pc.quantity || 1,
          collapsed: true,
        };
      }

      const nestedMaterials = hasDetail
        ? subDetail.components
            .filter(c => c.component_type === 'material')
            .map((comp, idx) => {
              const material = materialsById[comp.material_id];
              return {
                id: comp.material_id || comp.id || Date.now() + idx,
                name: comp.name,
                code: material?.code || '',
                price: material?.last_value || 0,
                color: MATERIAL_COLORS[idx % MATERIAL_COLORS.length],
                amount: comp.quantity || 1,
                currency: comp.currency || 'USD',
                unit: comp.unit || 'kg',
                source: comp.source_country || '',
                destination: comp.destination_country || '',
                selected: false,
              };
            })
        : [];

      return {
        id: pc.id || pc.product_ref_id || Date.now(),
        productRefId: pc.product_ref_id || null,
        name: pc.name,
        type: 'product',
        locked: isRef,
        components: nestedMaterials,
        subtotal: nestedMaterials.reduce((sum, c) => sum + c.price * c.amount, 0),
        currency: pc.currency || product.default_currency || 'USD',
        unit: pc.unit || 'unit',
        amount: pc.quantity || 1,
        collapsed: false,
      };
    });

    return {
      name: product.name || '',
      description: product.description || '',
      currency: product.default_currency || 'USD',
      unit: product.base_unit || 'Case',
      price: product.last_price_value || 0,
      amount: product.base_quantity || 1,
      imageUrl: product.image_url || null,
      components: builderComponents,
      subProducts: builderSubProducts,
    };
  }, [product, materialsById, subProductDetails]);

  // Model header display data
  const modelHeaderData = useMemo(() => {
    if (!product) return null;
    const baseQuantity = product.base_quantity || 1;
    const unitPrice = generalData?.last_price_value ?? product.last_price_value ?? 0;
    const absChange = generalData?.change_abs_value ?? null;

    return {
      id: product.id,
      name: generalData?.name || product.name,
      description: generalData?.description || product.description,
      price: unitPrice * baseQuantity,
      currency: generalData?.currency
        ? `${generalData.currency}/${generalData.value_unit || product.base_unit || 'mt'}`
        : product.last_price_currency || '',
      change: generalData?.change_pct_value ?? null,
      changeValue: absChange != null ? absChange * baseQuantity : null,
      date: generalData?.last_price_date_display || product.date,
      comparisonDate: generalData?.last_price_date_display ?? null,
      valueUnit: generalData?.value_unit || product.base_unit,
      vatPercent: generalData?.vat_percent,
      regulatory: product.sectors?.[0]?.name || '',
      lastUpdated: product.updated_at,
      imageUrl: product.image_url || null,
    };
  }, [product, generalData]);

  // ---------------------------------------------------------------------------
  // Event handlers
  // ---------------------------------------------------------------------------

  const handleToggleSubProductInChart = (section) => {
    if (!section.productRefId) return;
    toggleComparison({
      code: String(section.productRefId),
      name: section.title,
      price: section.price,
    });
  };

  const handleMouseDown = (e) => {
    if (!e.target.closest(".drag-handle")) e.preventDefault();
  };

  const handleDragStart = (index) => setDraggedItem(index);

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === index) return;
    const next = [...draggableComponents];
    const [moved] = next.splice(draggedItem, 1);
    next.splice(index, 0, moved);
    setDraggableComponents(next);
    setDraggedItem(index);
  };

  const handleDragEnd = () => setDraggedItem(null);

  const handleSaveModel = async (modelData) => {
    try {
      const apiPayload = transformModelDataToApiFormat(modelData);
      if (hasNestedSubProducts(modelData)) {
        await productService.updateNested(modelId, apiPayload);
      } else {
        await productService.update(modelId, apiPayload);
      }

      if (modelData.imageFile) {
        try {
          const formData = new FormData();
          formData.append('image_file', modelData.imageFile);
          await productService.uploadImage(modelId, formData);
        } catch {
          addToast('Model saved but image upload failed', 'warning');
        }
      }

      setShowModelBuilder(false);
      addToast('Model updated successfully', 'success');
      refetchProduct();
      refetchGeneral();
    } catch (err) {
      console.error('Error updating model:', err);
      addToast(`Failed to update model: ${err.message || 'Unknown error'}`, 'error', 6000);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="model_page">
        <div className="loading-state"><p>Loading model...</p></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="model_page">
        <div className="error-state"><p>Error loading model: {error.message}</p></div>
      </div>
    );
  }

  const componentMap = {
    ModelBreakdown: (
      <ModelBreakdownTable
        sections={breakdownSections || []}
        onToggleInChart={handleToggleSubProductInChart}
        activeComparisonCodes={comparisons.map(c => c.code)}
      />
    ),
    TableAndAverage: (
      <TableAndAverage materialCode={`PROD_${modelId}`} isProduct={true} productId={modelId} />
    ),
  };

  return (
    <div className="model_page">
      <div className="model_content">
        {/* Central column */}
        <div className="center_column">
          <ModelHeader modelData={modelHeaderData} />

          {comparisons.map(comparison => (
            <div key={comparison.id} className="mt-3">
              <ProductComparisonHeader comparison={comparison} onRemove={removeComparison} />
            </div>
          ))}

          <ModelMainChart
            material={product}
            comparisons={comparisons}
            onAddComparison={addComparison}
            onRemoveComparison={removeComparison}
            maxComparisons={MAX_COMPARISONS}
            currentMaterialCode={`PROD_${modelId}`}
            onOpenBuilder={() => setShowModelBuilder(true)}
            productId={modelId}
          />

          {draggableComponents.map((item, index) => (
            <div
              key={item.id}
              draggable
              onMouseDown={handleMouseDown}
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              className={`draggable-component${draggedItem === index ? ' dragging' : ''}`}
            >
              {componentMap[item.component]}
            </div>
          ))}
        </div>

        {/* Right column */}
        <div className="right_column">

          {/* Regulatory / Insights tabs */}
          <div className="news_comments">
            <ul className="news-tabs" role="tablist">
              <li className="news-tab-item" role="presentation">
                <button
                  className="news-tab-btn active"
                  id="regulatory-tab"
                  data-bs-toggle="pill"
                  data-bs-target="#regulatory"
                  type="button"
                  role="tab"
                  aria-controls="regulatory"
                  aria-selected="true"
                >
                  REGULATORY
                </button>
              </li>
              <li className="news-tab-item" role="presentation">
                <button
                  className="news-tab-btn"
                  id="insights-tab"
                  data-bs-toggle="pill"
                  data-bs-target="#insights"
                  type="button"
                  role="tab"
                  aria-controls="insights"
                  aria-selected="false"
                >
                  INSIGHTS
                </button>
              </li>
            </ul>

            <div className="news-tab-content tab-content mt-4" id="modelTabContent">
              <div className="tab-pane fade show active" id="regulatory" role="tabpanel" aria-labelledby="regulatory-tab">
                {regulatoryLoading ? (
                  <div className="mt-4 u-empty-state"><p>Loading regulatory data...</p></div>
                ) : transformedRegulatory.length > 0 ? (
                  transformedRegulatory.map(insight => (
                    <div className="mt-4" key={insight.id}>
                      <RegulatoryInsightCard insight={insight} />
                    </div>
                  ))
                ) : (
                  <div className="mt-4 u-empty-state"><p>No regulatory information available.</p></div>
                )}
              </div>

              <div className="tab-pane fade" id="insights" role="tabpanel" aria-labelledby="insights-tab">
                {insightsLoading ? (
                  <div className="mt-2 u-empty-state"><p>Loading insights...</p></div>
                ) : transformedInsights.length > 0 ? (
                  transformedInsights.map(insight => (
                    <div className="mt-2" key={insight.id}>
                      <RegulatoryInsightCard insight={insight} />
                    </div>
                  ))
                ) : (
                  <div className="mt-2 u-empty-state"><p>No insights available.</p></div>
                )}
              </div>
            </div>
          </div>

          {/* Classification Pie Chart */}
          {classificationLoading ? (
            <div className="news_comments mt-4 u-empty-state"><p>Loading classification...</p></div>
          ) : classificationData ? (
            <ClassificationPieChart
              classificationData={classificationData}
              sourcesData={sourcesData}
              sourcesLoading={sourcesLoading}
            />
          ) : null}

          {/* Price Breakdown */}
          <PriceBreakdownPanel priceBreakdownData={priceBreakdownData} />

          {/* Risk Assessment */}
          <div className="news_comments mt-4">
            <RiskAssessmentChart
              percentage={riskData?.risk_score || 0}
              title="Risk Assessment"
              description={riskData?.risk_text || ''}
              loading={riskLoading}
              error={riskError}
            />
          </div>
        </div>
      </div>

      <ToastContainer toasts={toasts} onRemove={removeToast} />

      <ModelBuilderOverlay
        isOpen={showModelBuilder}
        onClose={() => setShowModelBuilder(false)}
        modelId={modelId}
        initialData={builderInitialData}
        onSave={handleSaveModel}
      />
    </div>
  );
};

export default ModelPage;
